import axios from "axios";
import { ensureAssistantIdentity } from "./clientIdentity";
import { buildAssistantUrl } from "./assistantApi";
import { ASSISTANT_SESSION_KEY } from "./assistantStore";
import { AI_ASSISTANT_CONSTANTS } from "../constants/aiAssistantConstants";

export const getOrCreateSession = async ({ userId, sourceConversationId } = {}) => {
    try {
        const auth_token = localStorage.getItem("token");
        if (!auth_token) {
            return { success: false, error: "auth token missing" };
        }
        const stored = (() => {
            try {
                const raw = sessionStorage.getItem(ASSISTANT_SESSION_KEY);
                return raw ? JSON.parse(raw) : null;
            } catch {
                return null;
            }
        })();
        const matchUserId =
            userId != null &&
            stored?.clientId != null &&
            String(stored.clientId).includes(`--${userId}--`);
        if (stored?.clientId && stored?.conversationId && matchUserId) {
            return {
                clientId: stored.clientId,
                conversationId: stored.conversationId,
                ...(sourceConversationId && { sourceConversationId }),
            };
        }
        const result = await initiateSession({ userId, sourceConversationId });
        if (result.success && result.clientId && result.conversationId) {
            sessionStorage.setItem(
                ASSISTANT_SESSION_KEY,
                JSON.stringify({ clientId: result.clientId, conversationId: result.conversationId }),
            );
            return {
                clientId: result.clientId,
                conversationId: result.conversationId,
                ...(sourceConversationId && { sourceConversationId }),
            };
        }
        return result;
    } catch (error) {
        const detail =
            error?.response?.data ||
            error?.message ||
            "Unknown error during get or create session";
        console.error("[AI Assistant] getOrCreateSession failed", detail);
        return { success: false, error: detail };
    }
};

export const initiateSession = async (args, legacyCurrentScreenContext) => {
    try {
        const {
            userId,
            currentScreenContext,
            preferences,
            sourceConversationId,
        } =
            args && typeof args === "object" && !Array.isArray(args)
                ? args
                : {
                    userId: args,
                    currentScreenContext: legacyCurrentScreenContext,
                };
        const auth_token = localStorage.getItem("token");
        if (!auth_token) {
            throw new Error("auth token missing");
        }

        const { clientId } = ensureAssistantIdentity({ userId });

        const payload = { clientId };
        if (
            currentScreenContext &&
            typeof currentScreenContext === "object" &&
            !Array.isArray(currentScreenContext)
        ) {
            payload.currentScreenContext = currentScreenContext;
        }
        if (preferences && typeof preferences === "object" && !Array.isArray(preferences)) {
            payload.preferences = preferences;
        }
        if (sourceConversationId) {
            payload.sourceConversationId = sourceConversationId;
        }

        const initiationResponse = await axios.post(
            buildAssistantUrl("initiatesession"),
            payload,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: auth_token.startsWith("Bearer ") ? auth_token : `Bearer ${auth_token}`,
                },
                withCredentials: true,
            },
        );

        const conversationId = initiationResponse?.data?.session_id;
        if (!conversationId) {
            throw new Error("Backend did not return a session_id");
        }

        localStorage.setItem("sessionInitialized", "true");
        localStorage.removeItem("messages");
        sessionStorage.setItem(
            ASSISTANT_SESSION_KEY,
            JSON.stringify({ clientId, conversationId }),
        );

        // Broadcast session update so Ably context picks up the new conversationId
        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent(AI_ASSISTANT_CONSTANTS.EVENTS.SESSION_UPDATED, {
                    detail: { clientId, conversationId, meta: { source: "initiateSession" } },
                }),
            );
        }

        const suggestions = initiationResponse?.data?.suggestions || [];

        return { clientId, conversationId, success: true, status: initiationResponse?.status, suggestions };
    } catch (error) {
        const detail =
            error?.response?.data ||
            error?.message ||
            "Unknown error during initiate session";
        console.error("[AI Assistant] initiateSession failed", detail);
        return { success: false, error: detail };
    }
};
